import type { ChangeEvent } from 'react'
import { useCallback, useMemo } from 'react'

import type { DashboardSettingsResponseDto } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Toggle } from '@/components/ui/Toggle.tsx'
import { FormNumberField } from '@/components/ui/form/form-number-field'
import { useToast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'
import { debounce } from '@/utils/helpers'

import { useAutomationsMutations } from '../../hooks/use-automations-mutations'

interface PromotionReactivatorFormProps {
  data: Pick<
    DashboardSettingsResponseDto,
    'promotionReactivator' | 'defaultPromotionFinishDays'
  >
}

type PromotionFormValues = z.infer<typeof PromotionReactivatorFormSchema>

const PromotionReactivatorFormSchema = z.object({
  days: z.number(),
  enabled: z.boolean(),
})

export const PromotionReactivatorForm = ({
  data,
}: PromotionReactivatorFormProps) => {
  const { enablePromotion, setPromotionDays } = useAutomationsMutations()
  const { setSettingsData } = useSettingsContext()

  const toast = useToast()

  const defaultValues = useMemo(
    () => ({
      enabled: data.promotionReactivator,
      days: data.defaultPromotionFinishDays,
    }),
    [data],
  )

  const form = useForm<PromotionFormValues>({
    defaultValues,
    resolver: zodResolver(PromotionReactivatorFormSchema),
  })

  const handleEnabledChange = useCallback(
    (value: ChangeEvent<HTMLInputElement>) => {
      const checkedState = value.target.checked

      enablePromotion
        .mutateAsync({
          state: checkedState,
        })
        .then(() => {
          let state = 'enabled'

          if (checkedState === false) {
            state = 'disabled'
          }

          setSettingsData((prevState) => ({
            ...prevState,
            promotionReactivator: checkedState,
          }))

          toast.success(`Successfully ${state} Promotion Reactivator`)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedDaysChange = useCallback(
    debounce(setPromotionDays, 500),
    [],
  )

  return (
    <FormProvider {...form}>
      <div className="w-full">
        <Page
          title="Promotion Reactivator"
          description={
            <div className="flex items-center gap-1">
              <span>
                Automatically restart your OnlyFans promotion when it ends or
                all claim spots are taken.
              </span>
            </div>
          }
          headerOptions={
            <Controller
              name="enabled"
              control={form.control}
              render={({ field: { name, value, onChange } }) => (
                <Toggle
                  name={name}
                  checked={value}
                  onChange={(value) => {
                    onChange(value)
                    handleEnabledChange(value)
                  }}
                />
              )}
            />
          }
        >
          {form.getValues('enabled') && (
            <>
              <hr />

              <SettingsOption
                title="Restart Frequency"
                description={
                  <>
                    Set how often CreatorHero should restart your promotion if
                    it has ended or all claim spots are taken.
                    <span className="relative">
                      <Tooltip
                        className="absolute top-0 left-1"
                        contentClassName="max-w-56 text-left"
                        side="bottom"
                        content="Set the promotion duration and claim limits directly on OnlyFans. CreatorHero will automatically restart the same promotion when it ends or all claims are used."
                      >
                        <InfoIcon />
                      </Tooltip>
                    </span>
                  </>
                }
                biggerTitle
              >
                <FormNumberField
                  name="days"
                  postfix="Days"
                  onValueChange={handleDebouncedDaysChange}
                />
              </SettingsOption>
            </>
          )}
        </Page>
      </div>
    </FormProvider>
  )
}
